import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Group,
  Text,
  Loader,
  Center,
  Stack,
  NumberInput,
  Select,
  SegmentedControl,
  Badge,
  Checkbox,
  Button,
  Paper,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { itemsApi } from '../api/items';
import { categoriesApi } from '../api/categories';
import { useSelection } from '../hooks/useSelection';
import type { Item } from '../types';
import { pageContainer, listRow, listRowThumb, listRowThumbImg } from '../styles/theme.css';
import { getSemester, getSemesterRange } from '../utils/semester';

const MODE_KEY = 'old_items_mode';
const MONTHS_KEY = 'old_items_months';
const SEMESTER_KEY = 'old_items_semester';

function getStoredMonths(): number {
  const n = Number(localStorage.getItem(MONTHS_KEY));
  return n > 0 ? n : 3;
}

function ListRow({
  item,
  selected,
  onToggle,
  categoryLabel,
}: {
  item: Item;
  selected: boolean;
  onToggle: (id: number) => void;
  categoryLabel: string;
}) {
  const navigate = useNavigate();
  const sem = getSemester(item.found_date);
  return (
    <div
      className={listRow}
      onClick={() => navigate(`/items/${item.id}`)}
      style={{ background: selected ? 'var(--mantine-color-blue-0)' : undefined }}
    >
      <Checkbox
        checked={selected}
        onChange={() => onToggle(item.id)}
        onClick={(e) => e.stopPropagation()}
        size="sm"
        mr="xs"
      />
      <div className={listRowThumb}>
        {item.cover_image ? (
          <img src={item.cover_image} alt={item.title} className={listRowThumbImg} />
        ) : (
          '📦'
        )}
      </div>
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={600} lineClamp={1} style={{ flex: 1 }}>
            {item.title}
          </Text>
          <Badge size="xs" color="orange" variant="light" tt="none">
            {sem}
          </Badge>
        </Group>
        <Text size="xs" c="dimmed" lineClamp={1}>
          {[categoryLabel, item.color, item.brand].filter(Boolean).join(' · ')}
        </Text>
        <Text size="xs" c="dimmed">
          {item.storage_location && `📍 ${item.storage_location} · `}
          Fundet {new Date(item.found_date + 'T12:00:00').toLocaleDateString('da-DK')}
        </Text>
      </Stack>
    </div>
  );
}

export function OldItems() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'months' | 'semester'>(
    () => (localStorage.getItem(MODE_KEY) === 'semester' ? 'semester' : 'months')
  );
  const [months, setMonths] = useState<number>(getStoredMonths);
  const [semester, setSemester] = useState<string>(() => localStorage.getItem(SEMESTER_KEY) ?? '');
  const { selectedIds, toggleItem, toggleAll, clearSelection } = useSelection();

  const { data: catData } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.get,
    staleTime: Infinity,
  });

  const { data: items, isLoading, error } = useQuery({
    queryKey: ['items', { status: 'available' }],
    queryFn: () => itemsApi.list({ status: 'available' }),
  });

  function handleModeChange(v: string) {
    const m = v as 'months' | 'semester';
    setMode(m);
    clearSelection();
    localStorage.setItem(MODE_KEY, m);
  }

  function handleMonthsChange(value: string | number) {
    const n = Number(value);
    if (n > 0) {
      setMonths(n);
      clearSelection();
      localStorage.setItem(MONTHS_KEY, String(n));
    }
  }

  function handleSemesterChange(value: string | null) {
    if (value) {
      setSemester(value);
      clearSelection();
      localStorage.setItem(SEMESTER_KEY, value);
    }
  }

  // Derive available semesters from actual items, sorted newest first
  const availableSemesters = [...new Set((items ?? []).map((item) => getSemester(item.found_date)))]
    .sort()
    .reverse();

  // If stored semester isn't in the list (e.g. first load or all items gone), fall back to first
  const activeSemester = availableSemesters.includes(semester) ? semester : (availableSemesters[0] ?? '');

  const filtered = (() => {
    if (!items) return [];
    if (mode === 'months') {
      const cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - months);
      return items.filter((item) => new Date(item.found_date + 'T12:00:00') < cutoff);
    }
    const { start, end } = getSemesterRange(activeSemester);
    return items.filter((item) => {
      const d = new Date(item.found_date + 'T12:00:00');
      return d >= start && d <= end;
    });
  })();

  const discardMutation = useMutation({
    mutationFn: (ids: number[]) =>
      Promise.all(ids.map((id) => itemsApi.update(id, { status: 'discarded' }))),
    onSuccess: (_, ids) => {
      qc.invalidateQueries({ queryKey: ['items'] });
      clearSelection();
      notifications.show({
        message: `${ids.length} genstand${ids.length !== 1 ? 'e' : ''} markeret som kasseret`,
        color: 'green',
      });
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });

  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;
  const someSelected = selectedIds.size > 0;

  const summary =
    mode === 'months'
      ? `${filtered.length} genstand${filtered.length !== 1 ? 'e' : ''} ældre end ${months} måned${months !== 1 ? 'er' : ''}`
      : `${filtered.length} genstand${filtered.length !== 1 ? 'e' : ''} fra ${activeSemester}`;

  return (
    <div className={pageContainer}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Title order={2}>Gamle genstande</Title>
          <Group gap="sm" align="flex-end">
            <SegmentedControl
              value={mode}
              onChange={handleModeChange}
              data={[
                { label: 'Måneder', value: 'months' },
                { label: 'Semester', value: 'semester' },
              ]}
              size="xs"
            />
            {mode === 'months' && (
              <NumberInput
                label="Ældre end (måneder)"
                value={months}
                onChange={handleMonthsChange}
                min={1}
                max={120}
                w={170}
              />
            )}
            {mode === 'semester' && (
              <Select
                label="Semester"
                value={activeSemester}
                onChange={handleSemesterChange}
                data={availableSemesters.map((s) => ({ label: s, value: s }))}
                w={120}
              />
            )}
          </Group>
        </Group>

        {isLoading && (
          <Center py="xl">
            <Loader />
          </Center>
        )}

        {error && <Text c="red">Fejl ved indlæsning: {(error as Error).message}</Text>}

        {!isLoading && !error && (
          <Text size="sm" c="dimmed">
            {filtered.length === 0
              ? mode === 'months'
                ? `Ingen tilgængelige genstande ældre end ${months} måned${months !== 1 ? 'er' : ''}.`
                : `Ingen tilgængelige genstande fra ${activeSemester}.`
              : summary}
          </Text>
        )}

        {filtered.length > 0 && (
          <>
            <Group gap="xs">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={() => toggleAll(filtered.map((i) => i.id))}
                label={allSelected ? 'Fravælg alle' : 'Vælg alle'}
                size="sm"
              />
            </Group>

            <Stack gap={0}>
              {filtered.map((item) => (
                <ListRow
                  key={item.id}
                  item={item}
                  selected={selectedIds.has(item.id)}
                  onToggle={toggleItem}
                  categoryLabel={catData?.categories.find((c) => c.id === item.category_id)?.label_da ?? item.category_id}
                />
              ))}
            </Stack>
          </>
        )}
      </Stack>

      {someSelected && (
        <Paper
          withBorder
          shadow="md"
          p="md"
          style={{
            position: 'sticky',
            bottom: 16,
            marginTop: 16,
          }}
        >
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              {selectedIds.size} genstand{selectedIds.size !== 1 ? 'e' : ''} valgt
            </Text>
            <Group gap="xs">
              <Button size="xs" variant="subtle" color="gray" onClick={clearSelection}>
                Fravælg
              </Button>
              <Button
                size="xs"
                color="red"
                loading={discardMutation.isPending}
                onClick={() => discardMutation.mutate([...selectedIds])}
              >
                Kassér {selectedIds.size} genstand{selectedIds.size !== 1 ? 'e' : ''}
              </Button>
            </Group>
          </Group>
        </Paper>
      )}
    </div>
  );
}
